import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";
import ProjectsService from "@/services/ProjectsService";
import UserService from "@/services/UserService";
import ViewsService from "@/services/ViewsService";

import { writeLastViewId } from "@/hooks/useActiveView";

import { projectKeys, viewKeys } from "@/constants/CQueryKeys";
import { OnboardingDefaultRhythm } from "@/constants/index";

import { TOnboardingResult, TOnboardingValues, TProject } from "@/types";

import { promiseToast } from "@/modules/index";

/**
 * Creates everything the onboarding collected, in dependency order:
 * rhythm (mine) → own company (+ logo) → client → project. The project
 * is always there; company and client only when their step was filled.
 * Nothing is created before this runs, so an abandoned onboarding leaves
 * no trace and the "no project → /onboarding" redirect keeps working
 * (the view itself comes with the account, since phase 6).
 */
export const useOnboardingSubmit = () => {
	const queryClient = useQueryClient();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const submit = async (values: TOnboardingValues) => {
		setIsSubmitting(true);
		const run = async (): Promise<TOnboardingResult> => {
			const rhythm = values.rhythm ?? OnboardingDefaultRhythm;

			// The rhythm is the person's: one call, whatever they create
			// next.
			await UserService.updateSettings({
				workDays: rhythm.workDays,
				hoursPerDay: rhythm.hoursPerDay,
			});

			let companyName: string | undefined;
			if (values.company) {
				const { logo, ...companyFields } = values.company;
				const company = await CompaniesService.create({
					...companyFields,
					name: companyFields.name.trim(),
					type: "OWNER",
				});
				companyName = company.name;
				if (logo) {
					await CompaniesService.updateLogo(logo, company.id);
				}
			}

			let clientName: string | undefined;
			let clientId: number | undefined;
			const typedClientName = values.project.clientName.trim();
			if (typedClientName) {
				const client = await CompaniesService.create(
					{ name: typedClientName },
					"clients"
				);
				clientName = client.name;
				clientId = client.id;
			}

			// The first project is the typed client's, or internal until
			// one is attached from its settings.
			const project: TProject = await ProjectsService.createProject({
				name: values.project.name.trim(),
				color: values.project.color,
				description: values.project.description.trim() || undefined,
				...(clientId ? { clientId } : { internal: true }),
			});

			// The gate that sent us here watches the project list: it has
			// to see the new one, or leaving the onboarding bounces back.
			await queryClient.invalidateQueries({
				queryKey: projectKeys.all,
			});

			// The account's view was created at register ("Tous mes
			// projets"): refetching it is what the home page lands on.
			const views = await queryClient.fetchQuery({
				queryKey: viewKeys.all,
				queryFn: () => ViewsService.getViews(),
			});
			const view = views[0];
			if (view) writeLastViewId(view.id);

			return {
				viewName: view?.name ?? "",
				rhythm,
				projectName: project.name,
				projectColor: project.color,
				companyName,
				clientName,
			};
		};

		const prom = run();
		promiseToast(prom);
		return prom.finally(() => setIsSubmitting(false));
	};

	return { submit, isSubmitting };
};
